import React, { useMemo, useState } from 'react';

import {
	ActionSheet,
	ActionSheetAction,
} from '@/components/shared/ActionSheet';
import { FormInput } from '@/components/shared/Inputs';
import { Blurb } from '@/components/shared/Inputs/Blurb';
import { Label } from '@/components/shared/Inputs/Label';
import { Select } from '@/components/shared/Inputs/Select';
import {
	SettingCard,
	SettingSectionTitle,
} from '@/components/shared/SettingCard';
import { SingleActionLayout } from '@/components/shared/SingleActionLayout/SingleActionLayout';
import {
	DAYS_OF_WEEK,
	normalizeServiceSize,
	SERVICE_SIZE_OPTIONS,
} from '@/config/constants';
import {
	useCreateTeamServiceMutation,
	useDeleteTeamServiceMutation,
	useUpdateTeamServiceMutation,
} from '@/graphql';
import { hr24to12 } from '@/helpers/timeFormatter';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { useServices } from '@/providers/ServicesProvider';
import { Divider, List } from '@material-ui/core';
import { format } from 'date-fns';
import { Formik } from 'formik';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';

import { AccountDisplayProps } from '../../../AccountPanel';
import styles from './CreateUpdateService.module.scss';

type FormSchema = {
	name: string;
	serviceDay: number;
	startTime: string;
	serviceSize: string;
};

const validationSchema: Yup.SchemaOf<FormSchema> = Yup.object().shape({
	name: Yup.string().required().max(80, 'Can not be more than 80 characters'),
	serviceDay: Yup.number()
		.required('Service day is required')
		.min(0, 'Select a day')
		.max(6, 'Select a day'),
	startTime: Yup.string().required('Start time is required'),
	serviceSize: Yup.string().required('Service size is required'),
});

const normalizeStartAt = (startAt?: string | null): string => {
	if (!startAt) return '09:00';
	const trimmed = startAt.trim();
	if (trimmed.length >= 5) return trimmed.slice(0, 5);
	return trimmed;
};

export const CreateUpdateService: React.FC<AccountDisplayProps> = ({
	popDisplayState,
	setService,
	service,
}) => {
	const { mutateAsync } = useCreateTeamServiceMutation();
	const { mutateAsync: updateMutateAsync } = useUpdateTeamServiceMutation();
	const { mutateAsync: deleteMutateAsync } = useDeleteTeamServiceMutation();

	const { refetch } = useServices();
	const { enqueueSnackbar } = useSnackbar();
	const fireSaveService = useAnalyticsEvent('Services_Save_Service');
	const [actionOpen, setActionOpen] = useState(false);

	const initialValues: FormSchema = {
		name: service.name || '',
		serviceDay: service.day ?? 0,
		startTime: normalizeStartAt(service.startAt),
		serviceSize: normalizeServiceSize(service.size),
	};

	const serviceDayItems = useMemo(
		() =>
			DAYS_OF_WEEK.map((day, index) => ({
				label: day,
				value: index,
			})),
		[],
	);

	const startTimeItems = useMemo(() => {
		const items: { label: string; value: string }[] = [];
		for (let h = 6; h <= 23; h++) {
			for (let m = 0; m < (h === 23 ? 15 : 60); m += 15) {
				const value = format(new Date(0, 0, 0, h, m), 'HH:mm');
				items.push({
					label: hr24to12(value),
					value,
				});
			}
		}
		return items;
	}, []);

	return (
		<Formik
			key="asaphSignUn"
			initialValues={initialValues}
			validationSchema={validationSchema}
			onSubmit={async (values) => {
				const input = {
					day: values.serviceDay,
					name: values.name,
					startAt: values.startTime,
					size: values.serviceSize,
				};
				if (service.id) {
					await updateMutateAsync({
						serviceId: service.id,
						input,
					});
				} else {
					await mutateAsync({
						input,
					});
					enqueueSnackbar('Service created');
				}
				fireSaveService({ serviceSize: values.serviceSize });
				refetch();
				popDisplayState();
				setService({});
			}}
		>
			{({
				handleSubmit,
				handleChange,
				handleBlur,
				setFieldValue,
				setFieldTouched,
				values,
				errors,
				touched,
				isValid,
			}) => (
				<SingleActionLayout
					actionLabel="Save Service"
					actionDisabled={
						!isValid || Object.keys(touched).length === 0
					}
					onActionClick={handleSubmit}
				>
					<div>
						<Label>Service name</Label>
						<FormInput
							placeholder="e.g. Mornings"
							name="name"
							id="name"
							autoComplete="off"
							value={values.name}
							onChange={handleChange}
							onBlur={handleBlur}
							error={!!(touched.name && errors.name)}
						/>
						{touched.name && errors.name && (
							<Blurb isError>{errors.name}</Blurb>
						)}
						<div className="p-sm" />

						<Label>Service day</Label>
						<Select
							placeholder="Select day"
							items={serviceDayItems}
							onChange={(value) => {
								if (typeof value === 'number') {
									setFieldValue('serviceDay', value);
									setFieldTouched('serviceDay', true);
								}
							}}
							value={values.serviceDay}
							error={!!(touched.serviceDay && errors.serviceDay)}
						/>
						{touched.serviceDay && errors.serviceDay && (
							<Blurb isError>{errors.serviceDay}</Blurb>
						)}
						<div className="p-sm" />

						<Label>Service start time</Label>
						<Select
							placeholder="Select time"
							items={startTimeItems}
							onChange={(value) => {
								if (typeof value === 'string') {
									setFieldValue('startTime', value);
									setFieldTouched('startTime', true);
								}
							}}
							value={values.startTime}
							error={!!(touched.startTime && errors.startTime)}
						/>
						{touched.startTime && errors.startTime && (
							<Blurb isError>{errors.startTime}</Blurb>
						)}
						<div className="p-sm" />

						<Label
							tooltipHeading="Service size"
							tooltip="This helps us tailor insights and recommendations to your congregation size."
						>
							Service size
						</Label>
						<Select
							placeholder="Select size"
							items={SERVICE_SIZE_OPTIONS}
							onChange={(value) => {
								if (typeof value === 'string') {
									setFieldValue('serviceSize', value);
									setFieldTouched('serviceSize', true);
								}
							}}
							value={values.serviceSize}
							error={!!(touched.serviceSize && errors.serviceSize)}
						/>
						{touched.serviceSize && errors.serviceSize && (
							<Blurb isError>{errors.serviceSize}</Blurb>
						)}

						{service.id && (
							<>
								<Divider />
								<List
									className={
										styles['song-settings__lower-list']
									}
								>
									<SettingSectionTitle
										type="title"
										title="Manage service"
									/>
									<SettingCard
										type="setting"
										title="Archive service"
										onClick={() => setActionOpen(true)}
										red
										noChevron
										loading={false}
									/>
								</List>
								<Divider />
							</>
						)}

						<ActionSheet
							open={actionOpen}
							title={
								<span>
									Are you sure you want to archive{' '}
									{service.name}?
								</span>
							}
							onClose={() => setActionOpen(false)}
						>
							<ActionSheetAction
								label={'Remove for everyone'}
								onActionClick={async () => {
									try {
										if (service.id) {
											await deleteMutateAsync({
												serviceId: service.id,
											});
										}
										enqueueSnackbar('Service deleted.');
										popDisplayState();
										refetch();
									} catch (err) {
										enqueueSnackbar(
											'Something went wrong while delting this service.',
										);
									} finally {
										setActionOpen(false);
									}
								}}
								type={'danger'}
							/>
						</ActionSheet>
					</div>
				</SingleActionLayout>
			)}
		</Formik>
	);
};

export default CreateUpdateService;
